import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import {
  selectSchedule,
  selectScheduleMutationBlocked,
} from '../../../../../features/schedule/scheduleSelectors';
import {
  updateMatchDetailsAction,
  removeMatchFromScheduleAction,
} from '../../../../../features/schedule/scheduleActions';
import { ModalBox } from '../../../../../components';
import { IMatchScheduleItem } from '../../../../../types/schedule';
import { getColorForClass, getMatchDurationFromConstraints, getTournamentTimezone } from '../schedule.helpers';
import { ClassFormatEnum, ClassMatchType } from '../../../../../types/enums';
import { Class } from '../../../../../types/tournament';
import { CustomButton } from '../../../../../components';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

const getRoundText = (item: IMatchScheduleItem, classObject: Class) => {
  if (!classObject) return '';
  const isGroupFormat = classObject.format === ClassFormatEnum.GROUP;
  const isFinal = item.type === ClassMatchType.FINAL;
  const isWinnersBracket = item.bracket === 1;

  if (isFinal) {
    return item.isGold
      ? item.isAdditional
        ? 'Add. Final'
        : 'Final'
      : isGroupFormat
      ? 'Bronze'
      : 'Losers Final';
  }
  return (
    'Round ' + (isGroupFormat ? item.type : isWinnersBracket ? 'W' + item.type : 'L' + item.type)
  );
};

export const MatchEditModal = (props: ModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data } = useAppSelector((state) => state.modals);
  const match = data?.match as IMatchScheduleItem | undefined;

  const { tournamentInfo, classIdToObject, scheduleConstraints, unscheduledMatches } =
    useAppSelector(selectSchedule);
  const scheduleMutationBlocked = useAppSelector(selectScheduleMutationBlocked);

  const isUnscheduled = unscheduledMatches.some((m) => m.id === match?.id);

  const [dayKey, setDayKey] = useState<number>(0);
  const [courtId, setCourtId] = useState<number>(0);
  const [time, setTime] = useState<string>('');
  const [duration, setDuration] = useState<number>(25);
  const [initialDuration, setInitialDuration] = useState<number>(25);

  const prefix = 'modal-';

  const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
  // Set initial values as modal opens
  useEffect(() => {
    if (match && scheduleConstraints?.days?.length) {
      // Attempt duration from match directly
      let initialDurationCalc = match.duration;

      // Calculate it from planned slot dates only; match.endDate can be a completion timestamp.
      const slotStart = match.scheduleDate || match.startDate;
      if (!initialDurationCalc && slotStart) {
        const slotEnd = match.scheduleEndDate || null;
        if (slotEnd) {
          initialDurationCalc =
            (new Date(slotEnd).getTime() - new Date(slotStart).getTime()) / 60000;
        }
      }

      // Check constraints
      if (!initialDurationCalc || !Number.isFinite(initialDurationCalc) || initialDurationCalc <= 0) {
        initialDurationCalc = getMatchDurationFromConstraints(match, scheduleConstraints);
      }

      const finalDuration = initialDurationCalc || 25;
      setInitialDuration(finalDuration);
      setDuration(finalDuration);

      if (!isUnscheduled && slotStart) {
        const mDate = new Date(slotStart);

        const matchDayStr = formatInTimeZone(mDate, timeZone, 'yyyy-MM-dd');

        const dayMatch = scheduleConstraints.days.find((d) => {
          const dStartStr = formatInTimeZone(new Date(d.startTime), timeZone, 'yyyy-MM-dd');
          return matchDayStr === dStartStr;
        });

        setDayKey(dayMatch ? dayMatch.dayKey : scheduleConstraints.days[0].dayKey);
        setCourtId(match.court?.id || 0);

        setTime(formatInTimeZone(mDate, timeZone, 'HH:mm'));
      } else {
        setDayKey(scheduleConstraints.days[0].dayKey);
        setCourtId(scheduleConstraints.days[0].courts[0]?.id || 0);
        setTime('');
      }
    }
  }, [match, isUnscheduled, scheduleConstraints, timeZone]);

  const availableCourts =
    scheduleConstraints?.days.find((d) => d.dayKey === dayKey)?.courts ||
    tournamentInfo?.courts ||
    [];

  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDayKey = Number(e.target.value);
    setDayKey(newDayKey);

    const newAvailableCourts =
      scheduleConstraints?.days.find((d) => d.dayKey === newDayKey)?.courts ||
      tournamentInfo?.courts ||
      [];

    if (newAvailableCourts.length > 0 && !newAvailableCourts.some((c) => c.id === courtId)) {
      setCourtId(newAvailableCourts[0].id);
    }
  };

  const handleSave = async () => {
    if (!match || scheduleMutationBlocked) return;

    let newStartTimeIso: string | null = null;
    let selectedCourtId: number | null = null;

    if (time) {
      const dayInfo = scheduleConstraints?.days.find((d) => d.dayKey === dayKey);
      if (!dayInfo) return;

      const dateStr = formatInTimeZone(new Date(dayInfo.startTime), timeZone, 'yyyy-MM-dd');
      const localStr = `${dateStr}T${time}:00`;

      newStartTimeIso = fromZonedTime(localStr, timeZone).toISOString();
      selectedCourtId = courtId;
    }

    const success = await dispatch(
      updateMatchDetailsAction(match.id, selectedCourtId, newStartTimeIso, duration, t)
    );

    if (success && props.onHide) {
      props.onHide();
    }
  };

  const handleUnschedule = async () => {
    if (!match || scheduleMutationBlocked) return;

    const success = await dispatch(removeMatchFromScheduleAction(match.id, t));
    if (success && props.onHide) {
      props.onHide();
    }
  };

  if (!match) return null;

  const classColor = getColorForClass(+match.classId);
  const className = classIdToObject[match.classId]?.name || 'Unknown Class';
  const groupColor = match.tournamentGroup ? getColorForClass(+match.tournamentGroup.id) : '';

  const homeName = match.homeTeam?.name || match.homeTeamPlaceholder || 'TBD';
  const awayName = match.awayTeam?.name || match.awayTeamPlaceholder || 'TBD';
  const roundText = getRoundText(match, classIdToObject[match.classId]);

  // Dynamic button labeling and disabling logic
  let actionBtnText = t(prefix + 'save');
  let isSaveDisabled = false;

  if (isUnscheduled) {
    if (time) {
      actionBtnText = 'Schedule Match';
      isSaveDisabled = false; // Time provided -> allow scheduling
    } else {
      actionBtnText = 'Save Duration';
      // Disable if no time and no change to duration
      isSaveDisabled = duration === initialDuration;
    }
  } else {
    // For already scheduled matches, prevent saving if they clear the time box
    if (!time) {
      isSaveDisabled = true;
    }
  }

  if (scheduleMutationBlocked) {
    isSaveDisabled = true;
  }

  return (
    <ModalBox
      {...props}
      title={isUnscheduled && time ? 'Schedule Match' : 'Edit Match Details'}
      handleSave={handleSave}
      btnText={actionBtnText}
      isDisabled={isSaveDisabled}
    >
      <div className="d-flex flex-column gap-3 p-2">
        {scheduleMutationBlocked && (
          <div className="alert alert-info py-2 mb-0 small" role="status">
            {t('creativeDetail-scheduleReadOnlyBannerBody')}
          </div>
        )}
        <div className="p-3 border rounded bg-light">
          <div className="d-flex justify-content-center gap-2 mb-2">
            <span
              className="badge rounded-pill"
              style={{ backgroundColor: classColor, fontSize: '12px' }}
            >
              {className}
            </span>
            {match.tournamentGroup && (
              <span
                className="badge rounded-pill"
                style={{ backgroundColor: groupColor, fontSize: '12px', color: '#fff' }}
              >
                {match.tournamentGroup.name}
              </span>
            )}
          </div>

          <div className="text-center fw-bold fs-6 mb-1">
            {homeName}{' '}
            <span className="text-muted mx-2 fw-normal" style={{ fontSize: '12px' }}>
              vs
            </span>{' '}
            {awayName}
          </div>

          <div className="text-center text-muted" style={{ fontSize: '13px' }}>
            {roundText} • Match {match.matchId} • ID: {match.sortKey}
          </div>
        </div>

        <div className="row">
          <div className="col-6">
            <label className="form-label fw-bold" style={{ fontSize: '14px' }}>
              Day
            </label>
            <select
              className="form-select"
              value={dayKey}
              onChange={handleDayChange}
              disabled={scheduleMutationBlocked}
            >
              {scheduleConstraints?.days.map((d) => (
                <option key={d.dayKey} value={d.dayKey}>
                  Day {d.dayKey + 1} ({new Date(d.startTime).toLocaleDateString()})
                </option>
              ))}
            </select>
          </div>

          <div className="col-6">
            <label className="form-label fw-bold" style={{ fontSize: '14px' }}>
              Court
            </label>
            <select
              className="form-select"
              value={courtId}
              onChange={(e) => setCourtId(Number(e.target.value))}
              disabled={scheduleMutationBlocked}
            >
              {availableCourts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-6">
            <label className="form-label fw-bold" style={{ fontSize: '14px' }}>
              Start Time
            </label>
            <input
              type="time"
              className="form-control"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              disabled={scheduleMutationBlocked}
            />
          </div>

          <div className="col-6">
            <label className="form-label fw-bold" style={{ fontSize: '14px' }}>
              Duration (Mins)
            </label>
            <input
              type="number"
              className="form-control"
              value={duration}
              min="5"
              step="5"
              onChange={(e) => setDuration(Number(e.target.value))}
              disabled={scheduleMutationBlocked}
              required
            />
          </div>
        </div>

        {isUnscheduled && !time && duration === initialDuration && (
          <div className="text-muted fst-italic mt-1" style={{ fontSize: '13px' }}>
            * Please enter a <strong>Start Time</strong> to schedule this match, or adjust the
            duration to save it locally.
          </div>
        )}

        {!isUnscheduled && (
          <>
            <hr className="my-2" />
            <div className="d-flex justify-content-end">
              <CustomButton
                btnText={t(prefix + 'unscheduleMatch', 'Unschedule Match')}
                variant="outline-danger"
                cb={handleUnschedule}
                disabled={scheduleMutationBlocked}
              />
            </div>
          </>
        )}
      </div>
    </ModalBox>
  );
};